import React from 'react';

import {HTTPError} from 'coreUtils/adapters/sdk';

import {AppError} from './AppBootstrapContext';

/**
 * Props of {@link AppErrorBoundary}.
 */
type Props = {
	/** The brand `App` (header, page, footer). */
	children: React.ReactNode;
	/**
	 * Error detected outside of React while handling the request (`entry.server`), or serialized in
	 * `window.__ERROR__` when hydrating an error page (`entry.client`): the error page is shown from
	 * the first render.
	 */
	error?: AppError;
	/** Current pathname: the boundary clears its error when it changes (client navigation). */
	resetKey: string;
	/** The brand `app/components/_error` page. */
	ErrorPage: React.ComponentType<AppError>;
};

/**
 * State of {@link AppErrorBoundary}.
 */
type State = {
	/** Error currently displayed, if any. */
	error?: AppError;
	/** `resetKey` the current error belongs to. */
	resetKey: string;
};

const isProduction = process.env.NODE_ENV === 'production';

/**
 * Root error boundary of brands on React Router 7, in place of the PWA Kit `AppErrorBoundary`.
 *
 * Renders the brand error page for:
 * - render errors thrown by the app on the client (status from {@link HTTPError}, 500 otherwise);
 * - errors received through the `error` prop (request bootstrap on the server, hydration of an
 *   error page on the client).
 *
 * The error is cleared on navigation (`resetKey`). On the server, render errors are handled by
 * `entry.server`, which renders the document again with the `error` prop: React error boundaries
 * do not run during SSR.
 * @example
 * <AppErrorBoundary error={error} resetKey={location.pathname} ErrorPage={ErrorPage}>
 *     <App />
 * </AppErrorBoundary>
 */
class AppErrorBoundary extends React.Component<Props, State> {
	/**
	 * Starts with the error received from the request bootstrap, if any.
	 * @constructor
	 * @param {Props} props - boundary props
	 */
	constructor(props: Props) {
		super(props);
		this.state = {error: props.error, resetKey: props.resetKey};
	}

	/**
	 * Stores the error thrown while rendering, so that the error page is rendered instead of the app.
	 * @param {Error} err - error thrown by a descendant while rendering
	 * @returns {Partial<State>} - state with the error to display: status of an {@link HTTPError}, 500 otherwise
	 */
	static getDerivedStateFromError(err: Error): Partial<State> {
		return {
			error: {
				message: err.toString(),
				status: err instanceof HTTPError ? err.status : 500,
				stack: err.stack,
			},
		};
	}

	/**
	 * Clears the error when navigating to another page (`resetKey` changed).
	 * @param {Props} props - next props
	 * @param {State} state - current state
	 * @returns {Partial<State>|null} - state without error for the new `resetKey`, `null` when it did not change
	 */
	static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
		return props.resetKey !== state.resetKey ? {error: undefined, resetKey: props.resetKey} : null;
	}

	/**
	 * Renders the brand error page while there is an error (stack trace hidden in production),
	 * the app otherwise.
	 * @returns {React.ReactNode} - error page or app
	 */
	render(): React.ReactNode {
		const {error} = this.state;
		const {ErrorPage, children} = this.props;

		if (error) {
			return <ErrorPage message={error.message} status={error.status} stack={isProduction ? undefined : error.stack} />;
		}

		return children;
	}
}

export default AppErrorBoundary;
